import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, Loader2, Clock } from "lucide-react";
import { toast } from "sonner";

import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { BOOKING } from "@/constants/testIds";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUBJECTS = ["Chemistry", "Physics", "Biology", "Maths"];
const KEY_STAGES = [
  { id: "KS3", label: "KS3 · Year 7–9" },
  { id: "KS4", label: "KS4 · Year 10–11 (GCSE)" },
  { id: "KS5", label: "KS5 · Year 12–13 (A-Level Chemistry only)" },
];

export default function Booking() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: subject+date+time, 2: details, 3: confirm+pay
  const [subject, setSubject] = useState("");
  const [keyStage, setKeyStage] = useState("");
  const [date, setDate] = useState(undefined);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [time, setTime] = useState("");
  const [blockedDates, setBlockedDates] = useState([]);
  const [details, setDetails] = useState({
    student_name: "",
    parent_name: "",
    email: "",
    phone: "",
    notes: "",
  });
  const [booking, setBooking] = useState(null);
  const [creatingBooking, setCreatingBooking] = useState(false);
  const [redirecting, setRedirecting] = useState(false);

  const isoDate = date ? format(date, "yyyy-MM-dd") : "";

  useEffect(() => {
    axios
      .get(`${API}/blocked-dates`)
      .then((res) => setBlockedDates(res.data?.dates || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!isoDate) {
      setSlots([]);
      return;
    }
    setSlotsLoading(true);
    setTime("");
    axios
      .get(`${API}/availability`, { params: { date: isoDate } })
      .then((res) => setSlots(res.data.slots || []))
      .catch(() => toast.error("Could not load availability."))
      .finally(() => setSlotsLoading(false));
  }, [isoDate]);

  // Disable past dates, dates more than 90 days ahead, and blocked dates
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 90);
  const blockedDateObjects = blockedDates.map((d) => {
    const [y, m, day] = d.split("-").map(Number);
    return new Date(y, m - 1, day);
  });
  const disabledDays = [{ before: today }, { after: maxDate }, ...blockedDateObjects];

  const canContinueStep1 =
    subject && keyStage && date && time && !slotsLoading &&
    !(keyStage === "KS5" && subject !== "Chemistry");

  const ks5Conflict = keyStage === "KS5" && subject && subject !== "Chemistry";

  const handleCreateBooking = async () => {
    if (!details.student_name || !details.email) {
      toast.error("Student name and email are required.");
      return;
    }
    setCreatingBooking(true);
    try {
      const payload = {
        ...details,
        subject,
        key_stage: keyStage,
        session_date: isoDate,
        session_time: time,
      };
      const res = await axios.post(`${API}/bookings`, payload);
      setBooking(res.data);
      setStep(3);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not create booking.";
      toast.error(msg);
    } finally {
      setCreatingBooking(false);
    }
  };

  const handlePay = async () => {
    if (!booking) return;
    setRedirecting(true);
    try {
      const res = await axios.post(`${API}/payments/checkout`, {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Could not start checkout.");
        setRedirecting(false);
      }
    } catch (err) {
      toast.error("Payment could not be started. Please try again.");
      setRedirecting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fbfcfe] text-[#2a3b5c]">
      <Navbar />

      <section className="pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-6 md:px-12 lg:px-16">
          <button
            data-testid={BOOKING.backButton}
            onClick={() => (step === 1 ? navigate("/") : setStep(step - 1))}
            className="inline-flex items-center gap-2 text-sm text-[#5c6b85] hover:text-[#3d6fa8] transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {step === 1 ? "Back home" : "Back"}
          </button>

          <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#3d6fa8]">
                Book a session
              </div>
              <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
                {step === 1 && "Pick a subject and time"}
                {step === 2 && "A few quick details"}
                {step === 3 && "Confirm &amp; secure your slot"}
              </h1>
            </div>
            <div
              data-testid={BOOKING.stepIndicator}
              className="flex items-center gap-2 text-sm text-[#5c6b85]"
            >
              {[1, 2, 3].map((n) => (
                <span
                  key={n}
                  className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
                    n === step
                      ? "bg-[#8ab8e8] text-[#2a3b5c]"
                      : n < step
                      ? "bg-[#ffe89a] text-[#2a3b5c]"
                      : "bg-[#f0f6fc] text-[#5c6b85] border border-[#dce6f0]"
                  }`}
                >
                  {n < step ? <CheckCircle2 className="w-4 h-4" /> : n}
                </span>
              ))}
            </div>
          </div>

          {/* STEP 1 */}
          {step === 1 && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-8"
            >
              <div
                data-testid="booking-availability-info"
                className="lg:col-span-2 rounded-3xl bg-[#fff8df] border border-[#f0e3a8] p-6 md:p-7"
              >
                <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-6">
                  <div className="flex items-center gap-2 text-[#3d6fa8] shrink-0">
                    <Clock className="w-4 h-4" />
                    <span className="text-xs uppercase tracking-[0.2em] font-medium">
                      Times I’m available
                    </span>
                  </div>
                  <ul className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-5 text-sm text-[#2a3b5c] flex-1">
                    <li>
                      <span className="font-medium">Mon – Fri &amp; Sun</span>
                      <div className="text-[#5c6b85]">4:30 PM – 8:30 PM</div>
                    </li>
                    <li>
                      <span className="font-medium">Saturday</span>
                      <div className="text-[#5c6b85]">9 AM – 8 PM</div>
                    </li>
                    <li>
                      <span className="font-medium">All of August</span>
                      <div className="text-[#5c6b85]">9 AM – 6 PM (summer holidays)</div>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-[#f0f6fc] rounded-3xl p-8 border border-[#dce6f0] space-y-6">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                    Subject
                  </Label>
                  <Select value={subject} onValueChange={setSubject}>
                    <SelectTrigger
                      data-testid={BOOKING.subjectSelect}
                      className="mt-3 rounded-2xl h-12 bg-[#fbfcfe] border-[#dce6f0]"
                    >
                      <SelectValue placeholder="Choose a subject" />
                    </SelectTrigger>
                    <SelectContent>
                      {SUBJECTS.map((s) => (
                        <SelectItem key={s} value={s}>
                          {s}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                    Key stage / year
                  </Label>
                  <Select value={keyStage} onValueChange={setKeyStage}>
                    <SelectTrigger
                      data-testid={BOOKING.keyStageSelect}
                      className="mt-3 rounded-2xl h-12 bg-[#fbfcfe] border-[#dce6f0]"
                    >
                      <SelectValue placeholder="Choose the level" />
                    </SelectTrigger>
                    <SelectContent>
                      {KEY_STAGES.map((k) => (
                        <SelectItem key={k.id} value={k.id}>
                          {k.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {ks5Conflict && (
                    <p className="mt-2 text-sm text-[#3d6fa8]">
                      A-Level is available for Chemistry only. Please choose
                      Chemistry, or pick KS3/KS4 for {subject}.
                    </p>
                  )}
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                    Time slot
                  </Label>
                  {!date && (
                    <p className="mt-3 text-sm text-[#5c6b85]">
                      Pick a date first to see available times.
                    </p>
                  )}
                  {date && slotsLoading && (
                    <div className="mt-3 flex items-center gap-2 text-sm text-[#5c6b85]">
                      <Loader2 className="w-4 h-4 animate-spin" /> Loading times...
                    </div>
                  )}
                  {date && !slotsLoading && (
                    <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {slots.map((s) => (
                        <button
                          key={s.time}
                          data-testid={`${BOOKING.timeSlot}-${s.time}`}
                          disabled={!s.available}
                          onClick={() => setTime(s.time)}
                          className={`rounded-full py-2 px-3 text-sm transition-all ${
                            time === s.time
                              ? "bg-[#8ab8e8] text-[#2a3b5c]"
                              : s.available
                              ? "bg-[#fbfcfe] border border-[#dce6f0] text-[#2a3b5c] hover:border-[#8ab8e8]"
                              : "bg-[#e4eef7] text-[#5c6b85]/50 cursor-not-allowed line-through"
                          }`}
                        >
                          {s.time}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-[#f0f6fc] rounded-3xl p-6 border border-[#dce6f0] flex justify-center">
                <Calendar
                  data-testid={BOOKING.calendar}
                  mode="single"
                  selected={date}
                  onSelect={setDate}
                  disabled={disabledDays}
                  className="rounded-2xl bg-[#fbfcfe] p-3"
                />
              </div>

              <div className="lg:col-span-2 flex justify-end">
                <Button
                  data-testid={BOOKING.submitButton}
                  disabled={!canContinueStep1}
                  onClick={() => setStep(2)}
                  className="rounded-full px-7 py-6 text-base bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c] disabled:opacity-50"
                >
                  Continue
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5"
            >
              <div className="md:col-span-2 rounded-2xl bg-[#f0f6fc] border border-[#dce6f0] px-6 py-4 text-sm text-[#5c6b85]">
                <strong className="text-[#2a3b5c]">{subject}</strong> ·{" "}
                {KEY_STAGES.find((k) => k.id === keyStage)?.label} ·{" "}
                {date && format(date, "EEEE d MMM yyyy")} at {time}
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                  Student name
                </Label>
                <Input
                  data-testid={BOOKING.studentNameInput}
                  placeholder="Full name"
                  value={details.student_name}
                  onChange={(e) => setDetails({ ...details, student_name: e.target.value })}
                  className="mt-3 rounded-2xl h-12 bg-[#f0f6fc] border-[#dce6f0]"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                  Parent / guardian name
                </Label>
                <Input
                  data-testid={BOOKING.parentNameInput}
                  placeholder="Optional"
                  value={details.parent_name}
                  onChange={(e) => setDetails({ ...details, parent_name: e.target.value })}
                  className="mt-3 rounded-2xl h-12 bg-[#f0f6fc] border-[#dce6f0]"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                  Email
                </Label>
                <Input
                  data-testid={BOOKING.emailInput}
                  placeholder="you@example.com"
                  type="email"
                  value={details.email}
                  onChange={(e) => setDetails({ ...details, email: e.target.value })}
                  className="mt-3 rounded-2xl h-12 bg-[#f0f6fc] border-[#dce6f0]"
                />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                  Phone
                </Label>
                <Input
                  data-testid={BOOKING.phoneInput}
                  placeholder="Optional"
                  value={details.phone}
                  onChange={(e) => setDetails({ ...details, phone: e.target.value })}
                  className="mt-3 rounded-2xl h-12 bg-[#f0f6fc] border-[#dce6f0]"
                />
              </div>

              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-[#3d6fa8] font-medium">
                  Topics or notes
                </Label>
                <Textarea
                  data-testid={BOOKING.notesInput}
                  placeholder="What would you like to focus on?"
                  value={details.notes}
                  onChange={(e) => setDetails({ ...details, notes: e.target.value })}
                  className="mt-3 rounded-2xl min-h-[120px] bg-[#f0f6fc] border-[#dce6f0]"
                />
              </div>

              <div className="md:col-span-2 flex justify-end">
                <Button
                  data-testid={BOOKING.submitButton}
                  disabled={creatingBooking}
                  onClick={handleCreateBooking}
                  className="rounded-full px-7 py-6 text-base bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c]"
                >
                  {creatingBooking ? "Saving..." : "Continue to payment"}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          )}

          {/* STEP 3 */}
          {step === 3 && booking && (
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-8"
            >
              <div className="lg:col-span-3 bg-[#f0f6fc] rounded-3xl border border-[#dce6f0] p-8">
                <h3 className="font-heading text-xl font-semibold">Your session</h3>
                <dl className="mt-6 space-y-4 text-sm">
                  <Row label="Subject" value={booking.subject} />
                  <Row
                    label="Level"
                    value={KEY_STAGES.find((k) => k.id === booking.key_stage)?.label || booking.key_stage}
                  />
                  <Row
                    label="Date"
                    value={format(new Date(booking.session_date), "EEEE d MMM yyyy")}
                  />
                  <Row label="Time" value={`${booking.session_time} (60 min)`} />
                  <Row label="Student" value={booking.student_name} />
                  <Row label="Email" value={booking.email} />
                </dl>
              </div>

              <div className="lg:col-span-2">
                <div className="rounded-3xl border-2 border-[#8ab8e8] bg-[#fbfcfe] p-8 relative overflow-hidden">
                  <div className="absolute -top-16 -right-16 w-44 h-44 terracotta-blob" />
                  <div className="text-xs uppercase tracking-[0.2em] font-medium text-[#3d6fa8]">
                    Total
                  </div>
                  <div className="mt-3 flex items-baseline gap-2">
                    <span data-testid={BOOKING.totalAmount} className="font-heading text-5xl font-semibold">£{Number(booking.amount || 30).toFixed(2)}</span>
                    <span className="text-sm text-[#5c6b85]">GBP</span>
                  </div>
                  <p className="mt-4 text-sm text-[#5c6b85] leading-relaxed">
                    Pay securely with Stripe to confirm your slot. You’ll
                    receive an email receipt with your session link.
                  </p>
                  <Button
                    data-testid={BOOKING.payNowButton}
                    disabled={redirecting}
                    onClick={handlePay}
                    className="mt-7 w-full rounded-full py-6 text-base bg-[#8ab8e8] hover:bg-[#6fa1d6] text-[#2a3b5c]"
                  >
                    {redirecting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Redirecting to Stripe...
                      </>
                    ) : (
                      <>Pay £{Number(booking.amount || 30).toFixed(0)} &amp; confirm</>
                    )}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      </section>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-start justify-between gap-4 border-b border-[#dce6f0] pb-3">
    <dt className="text-xs uppercase tracking-[0.15em] text-[#5c6b85]">{label}</dt>
    <dd className="text-[#2a3b5c] font-medium text-right">{value}</dd>
  </div>
);
